import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Ruler } from "lucide-react";
import { fitFacts, productSpecs } from "../../../scripts/fit-facts.mjs";

interface FitFactsProps {
  description: string;
  /** The supplier's raw title: rug sizes and the leading-size check read it. */
  title: string;
  productType?: string;
}

/**
 * "Will it fit?" — the item's own sizes run through the calculators' rules.
 * Same function as the prerendered body, so shoppers and crawlers read the
 * same numbers. Renders nothing when the sizes are missing or implausible.
 */
export const FitFacts = ({ description, title, productType }: FitFactsProps) => {
  const fit = useMemo(
    () => fitFacts({ productType, title, specs: productSpecs(description, productType) }),
    [description, title, productType],
  );
  if (!fit) return null;

  return (
    <section className="border-t border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-12">
        <div className="flex items-center gap-2 mb-3">
          <Ruler className="h-5 w-5 text-foreground/40" />
          <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">Sizing</p>
        </div>
        <h2 className="font-sans text-lg font-semibold text-foreground mb-4">Will it fit?</h2>
        <ul className="space-y-2 max-w-3xl">
          {fit.facts.map((fact) => {
            const [label, ...rest] = fact.split(": ");
            return (
              <li key={fact} className="flex gap-2 text-[14px] text-foreground leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2" />
                <span>
                  {rest.length ? (
                    <>
                      <span className="font-semibold">{label}:</span> {rest.join(": ")}
                    </>
                  ) : (
                    fact
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="text-[13px] text-muted-foreground mt-4">
          From the supplier's item dimensions. {fit.intro}{" "}
          {fit.tools.map((t, i) => (
            <span key={t.href}>
              {i > 0 && " · "}
              <Link to={t.href} className="text-foreground underline underline-offset-2 hover:text-primary">
                {t.label}
              </Link>
            </span>
          ))}
        </p>
      </div>
    </section>
  );
};
